import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotifications } from "../common/stateDefs";
import {
  storePromotionCode,
  type StoredPromotionCode,
  validatePromotionCode,
} from "../common/promotionCodes";

const pendingPromotionCodeLookups = new Map<
  string,
  Promise<StoredPromotionCode | null>
>();

const getPromotionCodeLookup = (pathname: string, code: string) => {
  const existingLookup = pendingPromotionCodeLookups.get(pathname);
  if (existingLookup) {
    return existingLookup;
  }

  const lookup = validatePromotionCode(code)
    .catch((error) => {
      console.error("Error validating promotion code from path:", error);
      return null;
    })
    .finally(() => {
      pendingPromotionCodeLookups.delete(pathname);
    });

  pendingPromotionCodeLookups.set(pathname, lookup);
  return lookup;
};

export const UnknownPathRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const addNotification = useNotifications((state) => state.addNotification);

  useEffect(() => {
    const pathComponents = location.pathname.split("/").filter(Boolean);
    let cancelled = false;

    const redirectHome = () => {
      if (!cancelled) {
        navigate("/", { replace: true });
      }
    };

    if (pathComponents.length !== 1) {
      redirectHome();
      return () => {
        cancelled = true;
      };
    }

    const promotionCodeFromPath = decodeURIComponent(pathComponents[0]);

    void getPromotionCodeLookup(location.pathname, promotionCodeFromPath)
      .then((promotionCodeDetails) => {
        if (cancelled || !promotionCodeDetails) {
          return;
        }

        storePromotionCode(promotionCodeDetails);
        addNotification({
          id: `promotion-code-${promotionCodeDetails.promotionCode}-${Date.now()}`,
          title: "Promotion ready",
          type: "success",
          content: `Promotion code ${promotionCodeDetails.promotionCode} will be applied at checkout.`,
        });
      })
      .finally(() => {
        redirectHome();
      });

    return () => {
      cancelled = true;
    };
  }, [addNotification, location.pathname, navigate]);

  return null;
};
